/**
 * OmniLeagues — Family Directory ▸ Players tab.
 *
 * One row per player-season (the "All Seasons" view repeats a player once per
 * season). Reads /api/exports/players-directory/{org} (derived from
 * players.team_memberships — the SAME SOT as WP Mgmt ▸ Players). Search is
 * client-side, debounced, focus-stable, case-insensitive substring across
 * player name, parents, email and team level — within the selected season
 * (or All). (migration_log_0124)
 */
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import axios from 'axios';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { Loader2, User } from 'lucide-react';
import { SearchBox, useDebounced, matchesAny } from './familyDirectoryShared';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function PlayersDirectoryPanel({ orgId, isDark }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [sportFilter, setSportFilter] = useState('');
  const [seasonFilter, setSeasonFilter] = useState('');
  const seasonInit = useRef(false);
  const q = useDebounced(search, 250);

  const fetchData = useCallback(async () => {
    if (!orgId) return;
    try {
      const params = new URLSearchParams();
      if (sportFilter) params.set('sport_id', sportFilter);
      if (seasonFilter) params.set('season', seasonFilter);
      const { data } = await axios.get(`${API_URL}/api/exports/players-directory/${orgId}?${params}`);
      setData(data);
      if (!seasonInit.current) {
        seasonInit.current = true;
        const opts = data.season_options || [];
        if (data.current_season && opts.includes(data.current_season)) setSeasonFilter(data.current_season);
      }
    } catch (err) {
      console.error('Failed to load players directory:', err);
    } finally {
      setLoading(false);
    }
  }, [orgId, sportFilter, seasonFilter]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const filtered = useMemo(() => {
    if (!data) return [];
    return data.players.filter(p =>
      matchesAny(q, [p.name, p.parents, p.email, p.mobile, p.team_level, p.sport, p.season])
    );
  }, [data, q]);

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-amber-500" /></div>;
  if (!data) return null;

  const cardCls = isDark ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-slate-200';
  const text = isDark ? 'text-white' : 'text-slate-900';
  const muted = isDark ? 'text-slate-400' : 'text-slate-500';
  const rowHover = isDark ? 'hover:bg-slate-700/30' : 'hover:bg-amber-50/40';
  const border = isDark ? 'border-slate-700/40' : 'border-slate-200/60';
  const thBase = `text-left py-2 px-2 text-[11px] font-semibold uppercase tracking-wide ${muted}`;

  return (
    <div className="space-y-3" data-testid="players-directory-panel">
      <Card className={cardCls}>
        <CardContent className="p-3 flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-2">
            <User className={`h-4 w-4 ${isDark ? 'text-amber-400' : 'text-amber-500'}`} />
            <span className={`text-sm font-semibold ${text}`}>{filtered.length} Player-Seasons</span>
          </div>
          <SearchBox
            value={search}
            onChange={setSearch}
            placeholder="Search players, parents, or level…"
            isDark={isDark}
            testId="players-search"
          />
          <select
            value={seasonFilter}
            onChange={(e) => { setSeasonFilter(e.target.value); setLoading(true); }}
            className={`rounded-md px-3 py-2 text-sm border ${isDark ? 'bg-slate-800 border-slate-700 text-white' : 'bg-white border-slate-300'}`}
            data-testid="players-season-filter"
          >
            <option value="">All Seasons</option>
            {(data.season_options || []).map(s => <option key={s} value={s}>{s}</option>)}
          </select>
          <select
            value={sportFilter}
            onChange={(e) => { setSportFilter(e.target.value); setLoading(true); }}
            className={`rounded-md px-3 py-2 text-sm border ${isDark ? 'bg-slate-800 border-slate-700 text-white' : 'bg-white border-slate-300'}`}
            data-testid="players-sport-filter"
          >
            <option value="">All Sports</option>
            {(data.sport_options || []).map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
        </CardContent>
      </Card>

      <Card className={cardCls}>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-xs" data-testid="players-directory-table">
              <thead>
                <tr className={`border-b ${border}`}>
                  <th className={thBase}>Player</th>
                  <th className={thBase}>Season</th>
                  <th className={thBase}>Sport</th>
                  <th className={thBase}>Team Level</th>
                  <th className={thBase}>Birthday</th>
                  <th className={thBase}>Age</th>
                  <th className={thBase}>Mobile</th>
                  <th className={thBase}>Email</th>
                  <th className={thBase}>Parents</th>
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 && (
                  <tr>
                    <td colSpan={9} className={`py-6 text-center italic ${muted}`} data-testid="players-empty">
                      No players match this filter.
                    </td>
                  </tr>
                )}
                {filtered.map((p, idx) => (
                  <tr key={idx} className={`border-b ${border} ${rowHover}`} data-testid={`player-row-${idx}`}>
                    <td className={`py-2 px-2 align-top font-semibold ${text}`}>{p.name}</td>
                    <td className={`py-2 px-2 align-top ${muted} whitespace-nowrap`}>{p.season}</td>
                    <td className={`py-2 px-2 align-top ${muted}`}>{p.sport}</td>
                    <td className="py-2 px-2 align-top">
                      <Badge variant="outline" className={`text-[10px] ${isDark ? 'border-slate-600 text-slate-300' : 'border-slate-300 text-slate-600'}`}>
                        {p.team_level}
                      </Badge>
                    </td>
                    <td className={`py-2 px-2 align-top ${muted} whitespace-nowrap`}>{p.birthday || '—'}</td>
                    <td className={`py-2 px-2 align-top ${muted}`}>{p.age === '' || p.age == null ? '—' : p.age}</td>
                    <td className={`py-2 px-2 align-top ${muted} whitespace-nowrap`}>{p.mobile || '—'}</td>
                    <td className={`py-2 px-2 align-top ${muted}`}>
                      <span className="truncate inline-block max-w-[200px]">{p.email || '—'}</span>
                    </td>
                    <td className={`py-2 px-2 align-top ${muted}`}>
                      <span className="truncate inline-block max-w-[220px]">{p.parents || '—'}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
